import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import { useEffect, useState } from "react";

interface MetaAdsIntegration {
  id: string;
  instanceName: string;
  parameters?: {
    composioConnectedAccountId?: string;
  };
}

interface CustomAudience {
  id: string;
  name: string;
  description?: string;
  approximate_count?: number;
}

interface SequenceMetaAdsFormProps {
  actionParameters: any;
  setActionParameters: (params: any) => void;
  metaAdsIntegrations?: MetaAdsIntegration[];
  getToken: () => Promise<string>;
  hostname: string;
  companyId: string;
}

export default function SequenceMetaAdsForm({
  actionParameters,
  setActionParameters,
  metaAdsIntegrations = [],
  getToken,
  hostname,
  companyId,
}: SequenceMetaAdsFormProps) {
  const [customAudiences, setCustomAudiences] = useState<CustomAudience[]>([]);
  const [loadingAudiences, setLoadingAudiences] = useState(false);
  const [audienceError, setAudienceError] = useState<string | null>(null);

  // Initialize defaults
  useEffect(() => {
    const updates: any = {};
    let hasUpdates = false;

    if (actionParameters.includePhone === undefined) {
      updates.includePhone = true;
      hasUpdates = true;
    }
    if (actionParameters.includeName === undefined) {
      updates.includeName = true;
      hasUpdates = true;
    }

    if (hasUpdates) {
      setActionParameters({
        ...actionParameters,
        ...updates,
      });
    }
  }, [actionParameters, setActionParameters]);

  // Load custom audiences when integration is selected
  useEffect(() => {
    const loadAudiences = async () => {
      const integrationId = actionParameters.integrationInstanceId;
      if (!integrationId) {
        setCustomAudiences([]);
        return;
      }

      setLoadingAudiences(true);
      setAudienceError(null);

      try {
        const token = await getToken();
        const response = await fetch(
          `${hostname}/integrations/meta-ads/${integrationId}/audiences?companyId=${companyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setCustomAudiences(data.audiences || []);
        } else {
          const errorData = await response.json();
          setAudienceError(
            errorData.message || "Failed to load custom audiences"
          );
          setCustomAudiences([]);
        }
      } catch (error) {
        console.error("Error loading Meta Ads audiences:", error);
        setAudienceError("Failed to load custom audiences");
        setCustomAudiences([]);
      } finally {
        setLoadingAudiences(false);
      }
    };

    loadAudiences();
  }, [actionParameters.integrationInstanceId, getToken, hostname, companyId]);

  return (
    <Box>
      {/* Integration Selector */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          Meta Ads Account
          <Tooltip title="Select which Meta Ads account to use for adding members to custom audiences.">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Choose the Meta Ads account with access to your custom audiences.
        </Typography>
        {metaAdsIntegrations.length === 0 ? (
          <Typography variant="body2" color="error">
            No Meta Ads integrations found. Please connect a Meta Ads account in
            Integrations first.
          </Typography>
        ) : (
          <FormControl fullWidth>
            <InputLabel>Meta Ads Account</InputLabel>
            <Select
              label="Meta Ads Account"
              value={actionParameters.integrationInstanceId || ""}
              onChange={(e) =>
                setActionParameters({
                  ...actionParameters,
                  integrationInstanceId: e.target.value,
                  audienceId: "", // Reset audience when integration changes
                })
              }
            >
              {metaAdsIntegrations.map((integration) => (
                <MenuItem key={integration.id} value={integration.id}>
                  {integration.instanceName || "Meta Ads"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Custom Audience Selector */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          Custom Audience
          <Tooltip title="Select the custom audience to add members to. Members will be matched using their email address (and optionally phone and name).">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Select which custom audience to add sequence members to.
        </Typography>
        {!actionParameters.integrationInstanceId ? (
          <Typography variant="body2" color="text.secondary">
            Select a Meta Ads account first.
          </Typography>
        ) : loadingAudiences ? (
          <Typography variant="body2" color="text.secondary">
            Loading custom audiences...
          </Typography>
        ) : audienceError ? (
          <Box>
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {audienceError}
            </Typography>
            <TextField
              fullWidth
              label="Audience ID (manual entry)"
              placeholder="Enter your custom audience ID"
              value={actionParameters.audienceId || ""}
              onChange={(e) =>
                setActionParameters({
                  ...actionParameters,
                  audienceId: e.target.value,
                })
              }
              helperText="You can find this in your Meta Ads Manager under Audiences."
            />
          </Box>
        ) : customAudiences.length === 0 ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No custom audiences found, or enter the audience ID manually.
            </Typography>
            <TextField
              fullWidth
              label="Audience ID"
              placeholder="Enter your custom audience ID"
              value={actionParameters.audienceId || ""}
              onChange={(e) =>
                setActionParameters({
                  ...actionParameters,
                  audienceId: e.target.value,
                })
              }
              helperText="You can find this in your Meta Ads Manager under Audiences."
            />
          </Box>
        ) : (
          <FormControl fullWidth>
            <InputLabel>Custom Audience</InputLabel>
            <Select
              label="Custom Audience"
              value={actionParameters.audienceId || ""}
              onChange={(e) =>
                setActionParameters({
                  ...actionParameters,
                  audienceId: e.target.value,
                })
              }
            >
              {customAudiences.map((audience) => (
                <MenuItem key={audience.id} value={audience.id}>
                  {audience.name}
                  {audience.approximate_count !== undefined && (
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      (~{audience.approximate_count.toLocaleString()} users)
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Matching Options */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          Matching Options
          <Tooltip title="Additional data fields to include for better audience matching. Meta uses multi-key matching to improve match rates.">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Include additional data to improve match rates. Email is always
          included.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={actionParameters.includePhone !== false}
                onChange={(e) =>
                  setActionParameters({
                    ...actionParameters,
                    includePhone: e.target.checked,
                  })
                }
              />
            }
            label="Include phone numbers"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={actionParameters.includeName !== false}
                onChange={(e) =>
                  setActionParameters({
                    ...actionParameters,
                    includeName: e.target.checked,
                  })
                }
              />
            }
            label="Include first and last name"
          />
        </Box>
      </Box>

      {/* Info Box */}
      <Box
        sx={{
          p: 2,
          bgcolor: "action.hover",
          borderRadius: 1,
          mb: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          <strong>How it works:</strong> When this step executes, the member's
          email address (and optional phone/name) will be hashed and sent to
          Meta's Custom Audience API. All available email and phone fields from
          the member's data will be included to maximize match rates. Changes
          may take up to 24 hours to reflect in Meta Ads Manager.
        </Typography>
      </Box>
    </Box>
  );
}
